// ============================================================================
const API_BASE_PATH = '/tort'

interface GetAllTimeOffReqListParams {
  type?: string
  year: number
  month: number
  day: number
  page?: number
  perPage?: number
  timePeriod?: number
  sortOrder?: 'ASC' | 'DESC'
  sortColumn?: 'fromDate' | 'hoursRequested'
}

interface TimeOffRequestDetailsOptions {
  requestFrom?: 'REQUESTOR' | 'APPROVER'
  timezone?: string
}

const endpoints = {
  profile: `${API_BASE_PATH}/profile`,
  requester: {
    getBalance: `${API_BASE_PATH}/employee/time-off-balances`,
    getEmployeeInfoDetails: `${API_BASE_PATH}/employee/details`,
    getOtherReqDetails: (month: number, year: number) =>
      `${API_BASE_PATH}/time-off-details?month=${month}&year=${year}`,
    getAllTimeOffReqList: (params?: GetAllTimeOffReqListParams) => {
      // Handle undefined params with defaults
      if (!params) {
        const now = new Date()
        params = {
          year: now.getFullYear(),
          month: now.getMonth() + 1,
          day: now.getDate(),
          page: 1,
          perPage: 20,
        }
      }
      const {
        type,
        year,
        month,
        day,
        page = 1,
        perPage = 20,
        timePeriod,
        sortOrder,
        sortColumn,
      } = params
      const typeParam = type ? `&type=${type}` : ''
      const timePeriodParam = timePeriod ? `&timePeriod=${timePeriod}` : ''
      const sortOrderParam = sortOrder ? `&sortOrder=${sortOrder}` : ''
      const sortColumnParam = sortColumn ? `&sortColumn=${sortColumn}` : ''
      return `${API_BASE_PATH}/current/time-off-requests?date=${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}${typeParam}&page=${page}&perPage=${perPage}${timePeriodParam}${sortOrderParam}${sortColumnParam}`
    },
    createTimeOffRequest: `${API_BASE_PATH}/time-off-requests`,
    getTimeOffRequestDetails: (
      requestId: string,
      options?: TimeOffRequestDetailsOptions
    ) => {
      const encoded = encodeURIComponent(requestId)
      const url = `${API_BASE_PATH}/time-off-requests/${encoded}`

      if (!options?.requestFrom) {
        return url
      }

      const params = new URLSearchParams()
      params.set('requestFrom', options.requestFrom)
      if (options.timezone) {
        params.set('timezone', options.timezone)
      }

      return `${url}?${params.toString()}`
    },
    partialRetractTimeOffRequest: (requestId: string) =>
      `${API_BASE_PATH}/time-off-requests/${requestId}/partial-retract`,
    retractTimeOffRequest: (requestId: string) =>
      `${API_BASE_PATH}/time-off-requests/${requestId}/retract`,
    deleteTimeOffRequest: (requestId: string) =>
      `${API_BASE_PATH}/time-off-requests/${requestId}`,
    calendarView: `${API_BASE_PATH}/time-off-requests/calendar-view`,
    calendarFilters: `${API_BASE_PATH}/time-off-requests/calendar-filters`,
    resubmitRequestNoChange: (requestId: string) =>
      `${API_BASE_PATH}/time-off-requests/${requestId}/resubmit-no-change`,
    // Withdraw a time off request by ID
    withdrawTimeOffRequest: (torId: string) =>
      `${API_BASE_PATH}/time-off-requests/${encodeURIComponent(torId)}/withdraw`,
   requestorExport:`${API_BASE_PATH}/time-off-requests/export`
  },
  admin: {
    /** GET /tort/requestor-proxy/searchRP — search for requestor proxy records */
    searchRequestorProxy: `${API_BASE_PATH}/requestor-proxy/searchRP`,
    /** GET /tort/requestor-proxy/fetchCurrentRP — fetch current proxies for an approval group */
    fetchCurrentRequestorProxies: `${API_BASE_PATH}/requestor-proxy/fetchCurrentRP`,
    /** GET /tort/approver-proxy/searchAP — search approval groups with primary approver + merged approver proxies */
    searchApproverProxy: `${API_BASE_PATH}/approver-proxy/searchAP`,
    /** GET /tort/approver-proxy/fetchCurrentAP — fetch current approver proxies for an approval group */
    fetchCurrentApproverProxies: `${API_BASE_PATH}/approver-proxy/fetchCurrentAP`,
    /** GET /tort/approver-proxy/searchAvailableEmployees — search employees eligible to be added as an approver proxy for a group */
    searchAvailableEmployees: `${API_BASE_PATH}/approver-proxy/searchAvailableEmployees`,
    /** GET /tort/requestor-proxy/searchAvailableEmployees — search employees eligible to be added as a requestor/float proxy for a group */
    searchAvailableEmployeesRP: `${API_BASE_PATH}/requestor-proxy/searchAvailableEmployees`,
    /** POST /tort/requestor-proxy/assignRequestorProxies — save current requestor proxy assignments */
    assignRequestorProxies: `${API_BASE_PATH}/requestor-proxy/assignRequestorProxies`,
    /** POST /tort/approver-proxy/assignApproverProxies — save current approver proxy assignments */
    assignApproverProxies: `${API_BASE_PATH}/approver-proxy/assignApproverProxies`,
    /** GET /tort/float-employee/searchFE — search approval groups with primary approver + merged float employees */
    searchFloatEmployees: `${API_BASE_PATH}/float-employee/searchFE`,
    /** GET /tort/float-employee/fetchCurrentFE — fetch current float employees for an approval group */
    fetchCurrentFloatEmployees: `${API_BASE_PATH}/float-employee/fetchCurrentFE`,
    /** GET /tort/float-employee/searchAvailableEmployees — search employees eligible to be added as a float employee for a group */
    searchAvailableFloatEmployees: `${API_BASE_PATH}/float-employee/searchAvailableEmployees`,
    /** POST /tort/float-employee/assignFloatEmployees — save current float employee assignments */
    assignFloatEmployees: `${API_BASE_PATH}/float-employee/assignFloatEmployees`,
    /** GET /tort/manual-timeoff/searchEmployees — search employees within the same approval group as the requestor proxy */
    searchManualTimeOffEmployees: `${API_BASE_PATH}/manual-timeoff/searchEmployees`,
  },
  approver: {
    /** GET /tort/approver/time-off-requests/filter-options — returns filter dropdown options for the approver incoming requests view. */
    filterOptions: `${API_BASE_PATH}/approver/time-off-requests/filter-options`,
    /** POST /tort/approver/time-off-requests — returns paginated incoming requests for the approver. */
    requests: `${API_BASE_PATH}/approver/time-off-requests`,
    /** GET /tort/approver/time-off-requests/history — returns paginated history of approved/processed requests. */
    history: `${API_BASE_PATH}/approver/time-off-requests/history`,
    statusChange: `${API_BASE_PATH}/approver/time-off-requests/status-change`,
    calendarView: `${API_BASE_PATH}/approver/time-off-requests/calendar-view`,
   export: (type?: 'history') =>`${API_BASE_PATH}/approver/time-off-requests` +(type ? '/history' : '') +'/export',
  },
}

export default endpoints
export { API_BASE_PATH }
